// src/services/chatService.ts
import * as signalR from '@microsoft/signalr';
import env from '../env';

const API_BASE_URL = env.API_URL;
const SIGNALR_HUB_URL = env.SIGNALR_URL;

// Types
export enum UserType {
  Student = 1,
  Instructor = 2,
  Admin = 3
}

export interface Instructor {
  id: number;
  name: string;
  department: string;
  email: string;
}

export interface Conversation {
  id: number;
  message: string;
  studentId: number;
  instructorId: number;
  studentName: string;
  instructorName: string;
  lastMessage?: string;
  lastMessageTime?: string;
  unreadCount: number;
  createdAt: string;
}

export interface Message {
  id: number;
  conversationId: number;
  senderId: number;
  senderType: UserType;
  senderName: string;
  content: string;
  isRead: boolean;
  createdAt: string;
  attachments?: Attachment[];
}

export interface Attachment {
  id: number;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  contentType: string;
}

// API Service
class ChatApiService {
  private getAuthHeader(): HeadersInit {
    const token = sessionStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`
    };
  }

  async getChatsStatus(studentId: number): Promise<any[]> {
    const response = await fetch(
      `${API_BASE_URL}chat/instructors?studentId=${studentId}`,
      {
        headers: this.getAuthHeader()
      }
    );
    if (!response.ok) throw new Error('Failed to fetch instructors');
    return response.json();
  }

  async startConversation(
    studentId: number,
    instructorId: number,
    initialMessage: string
  ): Promise<Conversation> {
    const response = await fetch(`${API_BASE_URL}chat/conversations`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeader(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ studentId, instructorId, initialMessage })
    });
    if (!response.ok) throw new Error('Failed to start conversation');
    return response.json();
  }

  async getConversations(userId: number, userType: UserType): Promise<Conversation[]> {
    const response = await fetch(
      `${API_BASE_URL}chat/conversations?userId=${userId}&userType=${userType}`,
      {
        headers: this.getAuthHeader()
      }
    );
    if (!response.ok) throw new Error('Failed to fetch conversations');
    return response.json();
  }

  async getMessages(
    conversationId: number,
    userId: number,
    userType: UserType,
    take: number = 50
  ): Promise<Message[]> {
    const response = await fetch(
      `${API_BASE_URL}chat/conversations/${conversationId}/messages?userId=${userId}&userType=${userType}&take=${take}`,
      {
        headers: this.getAuthHeader()
      }
    );
    if (!response.ok) throw new Error('Failed to fetch messages');
    return response.json();
  }

  async sendMessage(request: any): Promise<Message> {
    const formData = new FormData();
    formData.append('conversationId', request.conversationId.toString());
    formData.append('senderId', request.senderId.toString());
    formData.append('senderType', request.senderType.toString());
    formData.append('content', request.content);
    if (request.attachment) {
      formData.append('attachment', request.attachment);
    }

    const response = await fetch(`${API_BASE_URL}chat/messages`, {
      method: 'POST',
      headers: this.getAuthHeader(),
      body: formData
    });
    if (!response.ok) throw new Error('Failed to send message');
    return response.json();
  }

  async markAsRead(conversationId: number, userId: number, userType: any): Promise<void> {
    const response = await fetch(
      `${API_BASE_URL}chat/conversations/${conversationId}/read`,
      {
        method: 'POST',
        headers: {
          ...this.getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId, userType })
      }
    );
    if (!response.ok) throw new Error('Failed to mark as read');
  }
}

// SignalR Service
class ChatSignalRService {
  private connection: signalR.HubConnection | null = null;
  private listeners: Map<string, Set<(data: any) => void>> = new Map();
  private isConnecting: boolean = false;

  async connect(): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      return;
    }

    if (this.isConnecting) {
      // Wait for existing connection attempt
      return new Promise((resolve) => {
        const checkConnection = setInterval(() => {
          if (this.connection?.state === signalR.HubConnectionState.Connected) {
            clearInterval(checkConnection);
            resolve();
          }
        }, 100);
      });
    }

    this.isConnecting = true;
    const token = sessionStorage.getItem('token');
    
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(SIGNALR_HUB_URL, {
        accessTokenFactory: () => token || ''
      })
      .withAutomaticReconnect()
      .build();

    // Register event handlers that emit to all listeners
    this.connection.on('ReceiveMessage', (data) => {
      //console.log('SignalR raw ReceiveMessage:', data);
      this.emit('ReceiveMessage', data);
    });

    this.connection.on('MessageSent', (data) => {
      this.emit('MessageSent', data);
    });

    this.connection.on('MessagesRead', (data) => {
      this.emit('MessagesRead', data);
    });

    this.connection.on('UserTyping', (data) => {
      this.emit('UserTyping', data);
    });

    this.connection.on('StopTyping', (data) => {
      this.emit('StopTyping', data);
    });

    try {
      await this.connection.start();
      //console.log('Connected to ChatHub');
      this.isConnecting = false;
    } catch (err) {
      console.error('Connection error:', err);
      this.isConnecting = false;
      throw err;
    }
  }

  async disconnect(): Promise<void> {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
    }
    // Clear all listeners on disconnect
    this.listeners.clear();
  }

  on(event: string, callback: (data: any) => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  off(event: string, callback: (data: any) => void): void {
    const listeners = this.listeners.get(event);
    if (listeners) {
      listeners.delete(callback);
    }
  }

  private emit(event: string, data: any): void {
    const listeners = this.listeners.get(event);
    //console.log(`Emitting ${event} to ${listeners?.size || 0} listeners`);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data);
        } catch (err) {
          console.error(`Error in ${event} listener:`, err);
        }
      });
    }
  }

  async userTyping(userId: number, conversationId: number, userType: string): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      await this.connection.invoke('UserTyping', userId, conversationId, userType);
    }
  }

  async stopTyping(userId: number, conversationId: number): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      await this.connection.invoke('StopTyping', userId, conversationId);
    }
  }

  // Note: SendMessage hub method doesn't exist on backend
  // The backend should broadcast ReceiveMessage after REST API saves the message
  // This method is kept for future use if backend adds the hub method
  async sendMessage(message: any): Promise<void> {
    // Backend doesn't have SendMessage hub method
    // Messages should be broadcast by the backend after saving via REST API
    // For now, we just log this - the sender sees the message via optimistic update
    //console.log('Message sent via REST API. Backend should broadcast to receiver:', message);
  }
}

// Export singleton instances
export const chatApi = new ChatApiService();
export const chatSignalR = new ChatSignalRService();
